import React, { useState } from "react";
import { motion } from "framer-motion";
import { Progress } from "../components/ui/progress";
import ShareStatsCard from "../components/ShareStatsCard";
import ShareableInfographics from "../components/ShareableInfographics";

const ShoppingPersonalityQuiz = ({ theme, goToInput }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [resultData, setResultData] = useState(null);
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Shopping personality assessment questions
  const questions = [
    {
      id: "shop_frequency",
      text: "How often do you shop for non-essential items?",
      options: [
        { value: "rarely", label: "Rarely (once every few months)" },
        { value: "occasionally", label: "Occasionally (once a month)" },
        { value: "regularly", label: "Regularly (few times a month)" },
        { value: "frequently", label: "Frequently (weekly or more)" }
      ]
    },
    {
      id: "emotional_state",
      text: "What emotional state most often triggers your shopping?",
      options: [
        { value: "happy", label: "Happiness/Celebration" },
        { value: "sad", label: "Sadness/Stress" },
        { value: "bored", label: "Boredom" },
        { value: "fomo", label: "Fear of missing out" }
      ]
    },
    {
      id: "purchase_planning",
      text: "How do you typically approach purchases?",
      options: [
        { value: "planned", label: "Carefully planned in advance" },
        { value: "researched", label: "Some research, then purchase" },
        { value: "browse", label: "Browse first, then decide" },
        { value: "impulse", label: "Impulse buys feel more exciting" }
      ]
    },
    {
      id: "budget_approach",
      text: "What's your approach to budgeting?",
      options: [
        { value: "strict", label: "Strict budget I rarely break" },
        { value: "flexible", label: "Flexible budget with some wiggle room" },
        { value: "general", label: "General guidelines but no strict limits" },
        { value: "none", label: "I don't really budget for shopping" }
      ]
    },
    {
      id: "price_sensitivity",
      text: "How important is price in your purchase decisions?",
      options: [
        { value: "very", label: "Very important - I always look for deals" },
        { value: "somewhat", label: "Somewhat important - I check prices but value matters more" },
        { value: "quality", label: "Secondary to quality - I'll pay more for better items" },
        { value: "irrelevant", label: "Not very important if I really want something" }
      ]
    },
    {
      id: "post_purchase",
      text: "How do you typically feel after a non-essential purchase?",
      options: [
        { value: "satisfied", label: "Satisfied - I made a good choice" },
        { value: "excited", label: "Excited and eager to use my new item" },
        { value: "mixed", label: "Mixed feelings - sometimes regretful" },
        { value: "guilty", label: "Often guilty or questioning my decision" }
      ]
    },
    {
      id: "unused_items",
      text: "How many unused or barely-used purchases do you have?",
      options: [
        { value: "few", label: "Very few - I use almost everything I buy" },
        { value: "some", label: "Some - occasionally buy things I don't use much" },
        { value: "many", label: "Quite a few - I have a tendency to accumulate" },
        { value: "lots", label: "Many - I often find unused purchases with tags still on" }
      ]
    },
    {
      id: "shopping_category",
      text: "What type of shopping gives you the most enjoyment?",
      options: [
        { value: "practical", label: "Practical items that improve my daily life" },
        { value: "experiences", label: "Things related to experiences or memories" },
        { value: "luxury", label: "Luxury or high-quality items" },
        { value: "trendy", label: "Trendy or unique items that others notice" }
      ]
    },
    {
      id: "browsing_habit",
      text: "How often do you browse online stores or shopping apps without a specific need?",
      options: [
        { value: "rarely", label: "Rarely or never" },
        { value: "occasionally", label: "Occasionally when I have free time" },
        { value: "regularly", label: "Regularly - it's a common pastime" },
        { value: "daily", label: "Daily - it's part of my routine" }
      ]
    },
    {
      id: "shopping_companions",
      text: "How do you prefer to shop?",
      options: [
        { value: "alone", label: "Alone and focused" },
        { value: "trusted", label: "With one trusted friend or partner" },
        { value: "social", label: "With a group - shopping is social" },
        { value: "online", label: "Online where nobody sees my choices" }
      ]
    }
  ];
  
  // Handle option selection
  const handleSelect = (value) => {
    const updatedAnswers = { ...answers, [questions[currentQuestion].id]: value };
    setAnswers(updatedAnswers);
    
    if (currentQuestion < questions.length - 1) {
      // Move to next question
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate results
      calculateResults(updatedAnswers);
    }
  };
  
  // Calculate assessment results
  const calculateResults = (finalAnswers) => {
    // Define personality types with point systems
    const personalityPoints = {
      "Mindful Spender": 0,
      "Practical Purchaser": 0,
      "Discount Daredevil": 0,
      "Luxury Justifier": 0,
      "Emotional Magpie": 0,
      "FOMO Shopper": 0,
      "Treat Yo'self Champion": 0,
      "Value Visionary": 0
    };
    
    // Score based on shopping frequency
    if (finalAnswers.shop_frequency === "rarely") {
      personalityPoints["Mindful Spender"] += 3;
      personalityPoints["Practical Purchaser"] += 2;
    } else if (finalAnswers.shop_frequency === "occasionally") {
      personalityPoints["Value Visionary"] += 2;
      personalityPoints["Practical Purchaser"] += 1;
    } else if (finalAnswers.shop_frequency === "regularly") {
      personalityPoints["Treat Yo'self Champion"] += 2;
      personalityPoints["Discount Daredevil"] += 1;
    } else if (finalAnswers.shop_frequency === "frequently") {
      personalityPoints["Emotional Magpie"] += 3;
      personalityPoints["FOMO Shopper"] += 2;
      personalityPoints["Luxury Justifier"] += 1;
    }
    
    // Score based on emotional triggers
    if (finalAnswers.emotional_state === "happy") {
      personalityPoints["Treat Yo'self Champion"] += 3;
      personalityPoints["Luxury Justifier"] += 1;
    } else if (finalAnswers.emotional_state === "sad") {
      personalityPoints["Emotional Magpie"] += 3;
      personalityPoints["Luxury Justifier"] += 1;
    } else if (finalAnswers.emotional_state === "bored") {
      personalityPoints["Discount Daredevil"] += 2;
      personalityPoints["Emotional Magpie"] += 2;
    } else if (finalAnswers.emotional_state === "fomo") {
      personalityPoints["FOMO Shopper"] += 3;
      personalityPoints["Emotional Magpie"] += 1;
    }
    
    // Score based on purchase planning
    if (finalAnswers.purchase_planning === "planned") {
      personalityPoints["Mindful Spender"] += 3;
      personalityPoints["Practical Purchaser"] += 2;
    } else if (finalAnswers.purchase_planning === "researched") {
      personalityPoints["Value Visionary"] += 3;
      personalityPoints["Mindful Spender"] += 1;
    } else if (finalAnswers.purchase_planning === "browse") {
      personalityPoints["Discount Daredevil"] += 2;
      personalityPoints["FOMO Shopper"] += 1;
    } else if (finalAnswers.purchase_planning === "impulse") {
      personalityPoints["Emotional Magpie"] += 3;
      personalityPoints["FOMO Shopper"] += 2;
    }
    
    // Score based on budget approach
    if (finalAnswers.budget_approach === "strict") {
      personalityPoints["Mindful Spender"] += 3;
      personalityPoints["Practical Purchaser"] += 2;
    } else if (finalAnswers.budget_approach === "flexible") {
      personalityPoints["Value Visionary"] += 2;
      personalityPoints["Discount Daredevil"] += 1;
    } else if (finalAnswers.budget_approach === "general") {
      personalityPoints["Treat Yo'self Champion"] += 2;
      personalityPoints["FOMO Shopper"] += 1;
    } else if (finalAnswers.budget_approach === "none") {
      personalityPoints["Luxury Justifier"] += 3;
      personalityPoints["Emotional Magpie"] += 2;
    }
    
    // Score based on price sensitivity
    if (finalAnswers.price_sensitivity === "very") {
      personalityPoints["Discount Daredevil"] += 3;
      personalityPoints["Practical Purchaser"] += 1;
    } else if (finalAnswers.price_sensitivity === "somewhat") {
      personalityPoints["Value Visionary"] += 3;
      personalityPoints["Mindful Spender"] += 1;
    } else if (finalAnswers.price_sensitivity === "quality") {
      personalityPoints["Luxury Justifier"] += 2;
      personalityPoints["Value Visionary"] += 1;
    } else if (finalAnswers.price_sensitivity === "irrelevant") {
      personalityPoints["Emotional Magpie"] += 2;
      personalityPoints["Luxury Justifier"] += 2;
    }
    
    // Score based on post-purchase feelings
    if (finalAnswers.post_purchase === "satisfied") {
      personalityPoints["Mindful Spender"] += 2;
      personalityPoints["Value Visionary"] += 2;
    } else if (finalAnswers.post_purchase === "excited") {
      personalityPoints["Treat Yo'self Champion"] += 3;
      personalityPoints["FOMO Shopper"] += 1;
    } else if (finalAnswers.post_purchase === "mixed") {
      personalityPoints["Discount Daredevil"] += 2;
      personalityPoints["FOMO Shopper"] += 1;
    } else if (finalAnswers.post_purchase === "guilty") {
      personalityPoints["Emotional Magpie"] += 3;
      personalityPoints["Luxury Justifier"] += 1;
    }
    
    // Score based on unused items
    if (finalAnswers.unused_items === "few") {
      personalityPoints["Mindful Spender"] += 3;
      personalityPoints["Practical Purchaser"] += 2;
    } else if (finalAnswers.unused_items === "some") {
      personalityPoints["Value Visionary"] += 2;
      personalityPoints["Treat Yo'self Champion"] += 1;
    } else if (finalAnswers.unused_items === "many") {
      personalityPoints["FOMO Shopper"] += 2;
      personalityPoints["Discount Daredevil"] += 2;
    } else if (finalAnswers.unused_items === "lots") {
      personalityPoints["Emotional Magpie"] += 3;
      personalityPoints["Luxury Justifier"] += 1;
    }
    
    // Score based on shopping category
    if (finalAnswers.shopping_category === "practical") {
      personalityPoints["Practical Purchaser"] += 3;
      personalityPoints["Mindful Spender"] += 1;
    } else if (finalAnswers.shopping_category === "experiences") {
      personalityPoints["Value Visionary"] += 2;
      personalityPoints["Treat Yo'self Champion"] += 2;
    } else if (finalAnswers.shopping_category === "luxury") {
      personalityPoints["Luxury Justifier"] += 3;
      personalityPoints["Value Visionary"] += 1;
    } else if (finalAnswers.shopping_category === "trendy") {
      personalityPoints["FOMO Shopper"] += 3;
      personalityPoints["Emotional Magpie"] += 1;
    }
    
    // Score based on browsing habits
    if (finalAnswers.browsing_habit === "rarely") {
      personalityPoints["Mindful Spender"] += 3;
      personalityPoints["Practical Purchaser"] += 2;
    } else if (finalAnswers.browsing_habit === "occasionally") {
      personalityPoints["Value Visionary"] += 2;
      personalityPoints["Luxury Justifier"] += 1;
    } else if (finalAnswers.browsing_habit === "regularly") {
      personalityPoints["Discount Daredevil"] += 2;
      personalityPoints["FOMO Shopper"] += 2;
    } else if (finalAnswers.browsing_habit === "daily") {
      personalityPoints["Emotional Magpie"] += 3;
      personalityPoints["FOMO Shopper"] += 1;
    }
    
    // Score based on shopping companions
    if (finalAnswers.shopping_companions === "alone") {
      personalityPoints["Mindful Spender"] += 2;
      personalityPoints["Luxury Justifier"] += 1;
    } else if (finalAnswers.shopping_companions === "trusted") {
      personalityPoints["Value Visionary"] += 2;
      personalityPoints["Practical Purchaser"] += 1;
    } else if (finalAnswers.shopping_companions === "social") {
      personalityPoints["Treat Yo'self Champion"] += 2;
      personalityPoints["FOMO Shopper"] += 2;
    } else if (finalAnswers.shopping_companions === "online") {
      personalityPoints["Discount Daredevil"] += 2;
      personalityPoints["Emotional Magpie"] += 2;
    }
    
    // Find top personality type
    const topPersonality = Object.entries(personalityPoints)
      .sort((a, b) => b[1] - a[1])[0][0];
    
    // Find secondary personality type
    const secondaryPersonality = Object.entries(personalityPoints)
      .sort((a, b) => b[1] - a[1])[1][0];
    
    // Prepare detailed result data
    const personalityInfo = {
      "Mindful Spender": {
        description: "You're thoughtful and deliberate in your shopping choices. You prioritize needs over wants and carefully consider purchases before making them.",
        emoji: "🧠",
        strengths: [
          "Rarely experience buyer's remorse",
          "Well-planned budget",
          "Thoughtful purchases that last",
          "Low impulse buy rate"
        ],
        challenges: [
          "May sometimes overthink purchases",
          "Might miss out on spontaneous joy",
          "Could be seen as too frugal by others"
        ],
        tips: [
          "Allow yourself small splurges occasionally",
          "Share your mindful shopping strategies with others",
          "Trust your process - it's working well for you"
        ]
      },
      "Practical Purchaser": {
        description: "You focus on utility and value with every purchase. You choose items that serve a specific purpose in your life.",
        emoji: "🛠️",
        strengths: [
          "High use rate for purchases",
          "Value-focused spending",
          "Minimal clutter and waste",
          "Clear purchasing criteria"
        ],
        challenges: [
          "May sometimes sacrifice joy for practicality",
          "Might delay replacing items past their usefulness",
          "Could miss out on quality upgrades"
        ],
        tips: [
          "Consider 'joy' as a practical value too",
          "Invest in higher quality for items you use daily",
          "Maintain your practical approach but allow occasional exceptions"
        ]
      },
      "Discount Daredevil": {
        description: "You love the thrill of finding a great deal. Sales and discounts are nearly irresistible to you, and you pride yourself on never paying full price.",
        emoji: "💯",
        strengths: [
          "Stretches budget effectively",
          "Finds great deals consistently",
          "Knows sales patterns and timing",
          "Maximizes value for money"
        ],
        challenges: [
          "May buy things solely because they're on sale",
          "Sometimes accumulates unused items",
          "Might spend more in total while 'saving' on each item"
        ],
        tips: [
          "Create a wishlist and only buy discounted items from it",
          "Calculate cost-per-use, not just discount percentage",
          "Set a monthly 'deal' budget regardless of sales encountered"
        ]
      },
      "Luxury Justifier": {
        description: "You appreciate quality and premium experiences. You believe in 'buy once, cry once' and can rationalize higher prices for items that bring you satisfaction.",
        emoji: "💎",
        strengths: [
          "Invests in quality items that last",
          "Appreciates craftsmanship and details",
          "Builds a curated collection over time",
          "Enjoys purchases deeply"
        ],
        challenges: [
          "Budget constraints vs. quality desires",
          "May overspend on brand names",
          "Sometimes justifies unnecessary premium purchases"
        ],
        tips: [
          "Research quality independent of brand prestige",
          "Create a luxury purchase fund with specific saving goals",
          "Consider second-hand premium items as a sustainable option"
        ]
      },
      "Emotional Magpie": {
        description: "Your shopping habits are closely tied to your emotional state. Shopping provides comfort, excitement, or distraction when you're feeling down or stressed.",
        emoji: "💸",
        strengths: [
          "Intuitive connection to what you like",
          "Shopping brings genuine emotional relief",
          "Finds unique items others might miss",
          "Passionate about purchases"
        ],
        challenges: [
          "High rate of regret or guilt after purchases",
          "Many unused items accumulating",
          "Emotional spending can impact budget"
        ],
        tips: [
          "Create a 24-hour rule for all non-essential purchases",
          "Develop alternative emotional outlets (walks, journaling)",
          "Track emotions before and after shopping to identify patterns",
          "Unsubscribe from store emails and uninstall shopping apps"
        ]
      },
      "FOMO Shopper": {
        description: "You're driven by trends and limited-time offers. You worry about missing out on the latest and greatest, and feel compelled to stay current with your purchases.",
        emoji: "👀",
        strengths: [
          "Stays current with trends and innovations",
          "Quick decision-making skills",
          "Open to trying new products and styles",
          "Socially connected through purchases"
        ],
        challenges: [
          "Higher spending due to constant updates",
          "Purchases may quickly become 'outdated'",
          "Influenced by marketing and social pressure"
        ],
        tips: [
          "Ask 'Will I still want this in 30 days?'",
          "Create a one-in-one-out rule for trendy purchases",
          "Follow trends selectively - choose which matter most to you",
          "Wait for the second wave of a trend to see if it has staying power"
        ]
      },
      "Treat Yo'self Champion": {
        description: "You see shopping as a reward system and celebration. You use purchases to mark achievements or boost your mood after accomplishing goals.",
        emoji: "🤩",
        strengths: [
          "Strong positive associations with shopping",
          "Creates joy and motivational rewards",
          "Celebrates achievements tangibly",
          "Less likely to experience regret"
        ],
        challenges: [
          "Might create too many 'reward' occasions",
          "Can justify unnecessary purchases as 'deserved'",
          "May overspend during celebrations"
        ],
        tips: [
          "Set clear milestones that earn specific rewards",
          "Create tiers of treats based on achievement size",
          "Consider non-material rewards (experiences, time off)",
          "Establish price limits for different celebration categories"
        ]
      },
      "Value Visionary": {
        description: "You take a balanced approach to shopping, seeking the intersection of quality, price, and longevity. You research thoroughly and make well-informed decisions.",
        emoji: "🔍",
        strengths: [
          "Optimizes for long-term value",
          "Research-driven purchase decisions",
          "Balances emotional and practical considerations",
          "Adaptable shopping strategy"
        ],
        challenges: [
          "Research can lead to analysis paralysis",
          "May spend too much time on minor purchases",
          "Sometimes struggles between quality and budget"
        ],
        tips: [
          "Set research time limits proportional to purchase price",
          "Trust your process and expertise",
          "Share your knowledge by helping friends with purchases",
          "Develop a quick-decision framework for smaller items"
        ]
      }
    };
    
    // Calculate trait percentages based on points
    const totalPoints = Object.values(personalityPoints).reduce((sum, points) => sum + points, 0);
    
    const traitPercentages = {};
    Object.entries(personalityPoints).forEach(([trait, points]) => {
      traitPercentages[trait] = Math.round((points / totalPoints) * 100);
    });
    
    // Prepare final result data
    setResultData({
      primaryType: topPersonality,
      secondaryType: secondaryPersonality,
      description: personalityInfo[topPersonality].description,
      emoji: personalityInfo[topPersonality].emoji,
      strengths: personalityInfo[topPersonality].strengths,
      challenges: personalityInfo[topPersonality].challenges,
      tips: personalityInfo[topPersonality].tips,
      traitPercentages: traitPercentages,
      secondaryDescription: `You also show strong traits of a ${secondaryPersonality}.`
    });
    
    // Show results
    setShowResults(true);
  };
  
  // Go back to previous question
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  // Restart the assessment
  const handleRestart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setResultData(null);
  };
  
  // Prepare data for ShareStatsCard component
  const prepareShareData = () => {
    if (!resultData) return null;
    
    return {
      quizTitle: "Shopping Personality Quiz",
      result: resultData.primaryType,
      resultEmoji: resultData.emoji,
      resultDescription: resultData.description,
      resultTagline: "Understanding your shopping style is the first step to mindful spending!",
      quizType: "shopping_personality"
    };
  };
  
  return (
    <motion.div 
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {!showResults ? (
        <>
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-3 dark:text-white">Shopping Personality Quiz</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Discover your unique shopping personality and get personalized tips for mindful spending.
            </p>
          </div>
          
          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
              <span>Question {currentQuestion + 1} of {questions.length}</span>
              <span>{Math.round((currentQuestion / questions.length) * 100)}% Complete</span>
            </div>
            <Progress
              value={(currentQuestion / questions.length) * 100}
              className="h-2 bg-gray-200 dark:bg-gray-700"
              style={{
                "--progress-indicator-color": 
                  theme === "mint" ? "#10b981" : 
                  theme === "pink" ? "#ec4899" : 
                  "#8b5cf6" // lavender
              }}
            />
          </div>
          
          {/* Current question */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 dark:text-white">
              {questions[currentQuestion].text}
            </h2>
            
            <div className="space-y-3">
              {questions[currentQuestion].options.map((option) => (
                <motion.button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full py-3 px-4 text-left rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition ${
                    answers[questions[currentQuestion].id] === option.value
                      ? `border-2 border-${theme}-500 dark:border-${theme}-400`
                      : ""
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {option.label}
                </motion.button>
              ))}
            </div>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-between">
            <motion.button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 ${
                currentQuestion === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
              whileHover={currentQuestion !== 0 ? { scale: 1.05 } : {}}
              whileTap={currentQuestion !== 0 ? { scale: 0.95 } : {}}
            >
              Previous
            </motion.button>
            
            <div className="text-sm text-gray-500 dark:text-gray-400 self-center">
              Your results are private and stored only on your device.
            </div>
          </div>
        </>
      ) : (
        <div>
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2 dark:text-white">Your Shopping Personality</h1>
            <div className="text-6xl mb-3">{resultData.emoji}</div>
            <h2 className="text-xl font-bold mb-1 dark:text-white">{resultData.primaryType}</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-1">
              {resultData.description}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {resultData.secondaryDescription}
            </p>
          </div>
          
          {/* Personality Breakdown */}
          <div className="mb-6">
            <h3 className="font-semibold mb-3 dark:text-white">Your Personality Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(resultData.traitPercentages)
                .sort(([, a], [, b]) => b - a)
                .map(([trait, percentage]) => (
                  <div key={trait} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium dark:text-white">{trait}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">{percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 h-2 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          trait === resultData.primaryType
                            ? "bg-blue-500"
                            : trait === resultData.secondaryType
                            ? "bg-purple-500"
                            : "bg-gray-400 dark:bg-gray-500"
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
          
          {/* Strengths & Challenges */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
              <h3 className="font-semibold mb-3 flex items-center dark:text-white">
                <span className="mr-2">💪</span> Your Shopping Strengths
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                {resultData.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-2">•</span> {strength}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
              <h3 className="font-semibold mb-3 flex items-center dark:text-white">
                <span className="mr-2">⚠️</span> Your Shopping Challenges
              </h3>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                {resultData.challenges.map((challenge, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span> {challenge}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          {/* Personalized Tips */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6">
            <h3 className="font-semibold mb-3 flex items-center dark:text-white">
              <span className="mr-2">💡</span> Personalized Shopping Tips
            </h3>
            <div className="space-y-3">
              {resultData.tips.map((tip, index) => (
                <div key={index} className="flex items-start">
                  <div className="bg-white dark:bg-gray-800 text-blue-500 rounded-full h-5 w-5 flex items-center justify-center text-xs font-bold mr-2 mt-0.5">
                    {index + 1}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">{tip}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Share Results Card */}
          <ShareStatsCard 
            purchaseData={prepareShareData()}
            theme={theme}
            userPersonality={resultData.primaryType}
            cardType="quiz"
          />

          <ShareableInfographics
            purchaseData={prepareShareData()}
            theme={theme}
            userPersonality={resultData.primaryType}
            cardType="quiz"
          />

          {/* Shop Recommendations */}
          {showResults && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-4 mb-6">
              <h3 className="font-semibold mb-2 dark:text-white flex items-center">
                <span className="mr-2">🛍️</span> Recommended Products for Your Style
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                Based on your {resultData.primaryType} personality, we've curated products that align with your mindful spending goals.
              </p>
              <motion.button
                onClick={() => window.location.href = '/shop'}
                className={`px-4 py-2 w-full rounded-lg text-white text-sm ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                View Recommendations
              </motion.button>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <motion.button
              onClick={handleRestart}
              className="py-3 rounded-xl border border-gray-300 dark:border-gray-600 font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Take Quiz Again
            </motion.button>
            
            <motion.button
              onClick={goToInput}
              className={`py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Track Your Purchases
            </motion.button>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This quiz is for entertainment and informational purposes only. Shopping habits exist on a spectrum and may change over time.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default ShoppingPersonalityQuiz;
